import {
  useEffect, useMemo, useRef, useState, useCallback,
} from "react";
import { Box, Stack, Typography, IconButton, Tooltip, useTheme } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

/**
 * TimelinePlayer — reusable timeline + playback engine.
 *
 * Design principles
 * ──────────────────
 * 1. The current playback progress (0..1) lives in a CSS custom property
 *    `--p` on the root. The RAF tick mutates it imperatively at 60fps.
 *    All motion (progress fill, playhead) is plain CSS `calc(var(--p) * …)`.
 *    React never re-renders during animation.
 *
 * 2. Event dots are rendered ONCE and never touched during play. They are
 *    positioned by absolute timestamp percent. Selection highlight is
 *    expressed via `data-active` toggled imperatively when the playhead
 *    crosses an event (not per frame).
 *
 * 3. The scrub slider is a native `<input type="range">` styled to match the
 *    rest of the dashboard. Native range inputs do not allocate React state
 *    on every drag tick — they emit `input` events directly. The component
 *    listens and updates `--p` imperatively while the user drags.
 *
 * 4. The only React state is: `isPlaying`, `speed`, and the active event id
 *    (which changes a few times per second at most).
 *
 * Props
 * ─────
 * events:           [{ id, timestamp, ...rest }] — required, must have unique id
 * getColor:         (event) => CSS color string (default: theme primary)
 * height:           pixels for the timeline track (default 140)
 * baseDurationMs:   full sweep duration at 1x (default 12000)
 * defaultSpeed:     0.25–2 (default 1)
 * onActiveChange:   (event | null) => void — fired on event crossings only
 * renderTooltip:    (event) => ReactNode — custom dot tooltip (optional)
 * autoStart:        boolean (default false)
 * showScrubber:     boolean (default true)
 */
const TimelinePlayer = ({
  events = [],
  getColor,
  height = 220,
  baseDurationMs = 12000,
  defaultSpeed = 1,
  onActiveChange,
  renderTooltip,
  autoStart = false,
  showScrubber = true,
}) => {
  const theme = useTheme();
  const accent = theme.palette.primary?.main || "#459095";
  const rail = theme.palette.action?.disabledBackground || "#9b9b9b55";

  // ── Build stable item list with positions (0..1) ────────────────────────
  const items = useMemo(() => {
    const valid = (events || [])
      .map((e) => {
        const ts = e?.timestamp instanceof Date
          ? e.timestamp.getTime()
          : (typeof e?.timestamp === "number" ? e.timestamp : Date.parse(e?.timestamp));
        return Number.isFinite(ts) ? { ...e, _ts: ts } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a._ts - b._ts);
    if (!valid.length) return { list: [], minTs: 0, maxTs: 0, range: 1 };
    const minTs = valid[0]._ts;
    const maxTs = valid[valid.length - 1]._ts;
    const range = Math.max(maxTs - minTs, 1);
    const list = valid.map((e, i) => ({
      ...e,
      _idx: i,
      _p: (e._ts - minTs) / range,        // 0..1 progress
    }));
    return { list, minTs, maxTs, range };
  }, [events]);

  // ── Refs (no rerender) ──────────────────────────────────────────────────
  const rootRef = useRef(null);
  const scrollRef = useRef(null);
  const progressRef = useRef(0);
  const rafRef = useRef(0);
  const playStartPerfRef = useRef(0);
  const playStartProgRef = useRef(0);
  const speedRef = useRef(defaultSpeed);
  const lastIdxRef = useRef(-1);
  const dotsRef = useRef(new Map());      // id → DOM node

  // ── Low-frequency state ─────────────────────────────────────────────────
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(defaultSpeed);

  // ── Notify parent on cross + toggle .active class on dots ───────────────
  const commitIndex = useCallback(
    (idx) => {
      if (idx === lastIdxRef.current) return;
      const prev = lastIdxRef.current;
      lastIdxRef.current = idx;
      // Toggle data-active on previous + new dot (DOM only, no React)
      if (prev >= 0) {
        const prevId = items.list[prev]?.id;
        const node = prevId && dotsRef.current.get(prevId);
        if (node) node.dataset.active = "false";
      }
      if (idx >= 0) {
        const id = items.list[idx]?.id;
        const node = id && dotsRef.current.get(id);
        if (node) node.dataset.active = "true";
        onActiveChange?.(items.list[idx]);
      } else {
        onActiveChange?.(null);
      }
    },
    [items.list, onActiveChange]
  );

  // ── Binary search: index of last event with progress <= p ───────────────
  const indexAtProgress = useCallback(
    (p) => {
      const arr = items.list;
      if (!arr.length) return -1;
      let lo = 0, hi = arr.length - 1, ans = 0;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (arr[mid]._p <= p) { ans = mid; lo = mid + 1; }
        else hi = mid - 1;
      }
      return ans;
    },
    [items.list]
  );

  // ── Imperative DOM updates: CSS var + auto-scroll ───────────────────────
  const applyProgress = useCallback((p) => {
    const root = rootRef.current;
    if (root) root.style.setProperty("--p", String(p));
    // Auto-scroll: keep playhead in view (only if the track overflows)
    const sc = scrollRef.current;
    if (sc && sc.scrollWidth > sc.clientWidth) {
      const headX = p * sc.scrollWidth;
      const margin = 120;
      const left = sc.scrollLeft;
      const right = left + sc.clientWidth;
      if (headX < left + margin || headX > right - margin) {
        sc.scrollLeft = Math.max(0, Math.min(headX - sc.clientWidth / 2, sc.scrollWidth - sc.clientWidth));
      }
    }
  }, []);

  // ── Playback control ────────────────────────────────────────────────────
  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    setIsPlaying(false);
  }, []);

  const seek = useCallback(
    (p) => {
      const clamped = Math.max(0, Math.min(1, p));
      progressRef.current = clamped;
      applyProgress(clamped);
      const idx = indexAtProgress(clamped);
      commitIndex(idx);
    },
    [applyProgress, indexAtProgress, commitIndex]
  );

  const play = useCallback(() => {
    if (!items.list.length) return;
    if (progressRef.current >= 1) progressRef.current = 0;
    playStartPerfRef.current = performance.now();
    playStartProgRef.current = progressRef.current;
    speedRef.current = Math.max(0.25, Math.min(2, speed));
    setIsPlaying(true);

    const tick = () => {
      const now = performance.now();
      const elapsed = (now - playStartPerfRef.current) * speedRef.current;
      const p = Math.min(1, playStartProgRef.current + elapsed / baseDurationMs);
      progressRef.current = p;
      applyProgress(p);
      commitIndex(indexAtProgress(p));
      if (p >= 1) {
        rafRef.current = 0;
        setIsPlaying(false);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
  }, [items.list, applyProgress, indexAtProgress, commitIndex, speed, baseDurationMs]);

  // ── Speed changes mid-play: rebase to avoid skip ───────────────────────
  useEffect(() => {
    speedRef.current = Math.max(0.25, Math.min(2, speed));
    if (rafRef.current) {
      playStartPerfRef.current = performance.now();
      playStartProgRef.current = progressRef.current;
    }
  }, [speed]);

  // ── Reset on event list change + auto-start ─────────────────────────────
  useEffect(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    progressRef.current = 0;
    lastIdxRef.current = -1;
    if (rootRef.current) rootRef.current.style.setProperty("--p", "0");
    if (items.list.length) {
      // Pre-select first event without animating
      commitIndex(0);
    } else {
      commitIndex(-1);
    }
    setIsPlaying(false);
    if (autoStart && items.list.length) {
      // microtask so DOM is settled
      Promise.resolve().then(play);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.list]);

  // ── Cleanup ─────────────────────────────────────────────────────────────
  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  // ── Track scrubbing: click + drag directly on the rail (no slider) ─────
  const seekFromPointer = useCallback((clientX) => {
    const sc = scrollRef.current;
    if (!sc) return;
    const inner = sc.firstElementChild;
    if (!inner) return;
    const rect = inner.getBoundingClientRect();
    const usable = Math.max(rect.width - TRACK_PADDING_X * 2, 1);
    const x = clientX - rect.left - TRACK_PADDING_X;
    seek(x / usable);
  }, [seek]);

  const onTrackPointerDown = useCallback((e) => {
    if (!items.list.length) return;
    // Ignore clicks on dots — they have their own onClick handler.
    if (e.target?.dataset?.active !== undefined) return;
    stop();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    seekFromPointer(e.clientX);
    const onMove = (ev) => seekFromPointer(ev.clientX);
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [items.list, stop, seekFromPointer]);

  // ── Step controls ───────────────────────────────────────────────────────
  const stepBy = useCallback(
    (delta) => {
      stop();
      const cur = lastIdxRef.current >= 0 ? lastIdxRef.current : 0;
      const next = Math.max(0, Math.min(items.list.length - 1, cur + delta));
      const ev = items.list[next];
      if (ev) seek(ev._p);
    },
    [stop, seek, items.list]
  );

  const restart = useCallback(() => {
    stop();
    seek(0);
  }, [stop, seek]);

  // ── Layout constants ────────────────────────────────────────────────────
  const TRACK_PADDING_X = 24;
  const DOT_SIZE = 14;
  // Reserve room for: controls row (~40) + min/max labels under track (~20).
  const trackHeight = Math.max(80, height - 60);

  const hasEvents = items.list.length > 0;

  return (
    <Box
      ref={rootRef}
      style={{ "--p": "0", "--accent": accent, "--pad-x": `${TRACK_PADDING_X}px` }}
      sx={{ width: "100%" }}
    >
      {/* ── Controls row ──────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Tooltip title={isPlaying ? "Pause" : "Play"}>
          <span>
            <IconButton
              size="small"
              disabled={!hasEvents}
              onClick={() => (isPlaying ? stop() : play())}
              sx={{ border: "1px solid", borderColor: "divider" }}
            >
              {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Previous">
          <span>
            <IconButton size="small" disabled={!hasEvents} onClick={() => stepBy(-1)}>
              <SkipPreviousIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Next">
          <span>
            <IconButton size="small" disabled={!hasEvents} onClick={() => stepBy(1)}>
              <SkipNextIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Restart">
          <span>
            <IconButton size="small" disabled={!hasEvents} onClick={restart}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {/* Speed control — small native select, decoupled from playback */}
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            Speed
          </Typography>
          {[0.5, 1, 1.5, 2].map((s) => (
            <Box
              key={s}
              role="button"
              tabIndex={0}
              onClick={() => setSpeed(s)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setSpeed(s)}
              sx={{
                px: 0.8, py: 0.2, borderRadius: 1, cursor: "pointer",
                fontSize: 11, fontWeight: 700,
                color: speed === s ? "#fff" : "text.secondary",
                bgcolor: speed === s ? "var(--accent)" : "transparent",
                border: "1px solid",
                borderColor: speed === s ? "var(--accent)" : "divider",
                "&:hover": { borderColor: "var(--accent)" },
              }}
            >
              {s}x
            </Box>
          ))}
        </Stack>
      </Stack>

      {/* ── Track (click + drag to seek directly on the rail) ─────────── */}
      <Box
        ref={scrollRef}
        onPointerDown={onTrackPointerDown}
        sx={{
          position: "relative",
          height: trackHeight,
          mt: 2.5,
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
          cursor: hasEvents ? "pointer" : "default",
          userSelect: "none",
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: "100%",
            minWidth: Math.max(640, items.list.length * 80),
            px: `${TRACK_PADDING_X}px`,
          }}
        >
          {/* Rail */}
          <Box
            sx={{
              position: "absolute",
              left: `${TRACK_PADDING_X}px`,
              right: `${TRACK_PADDING_X}px`,
              top: "50%",
              height: 4,
              borderRadius: 999,
              backgroundColor: rail,
              transform: "translateY(-50%)",
            }}
          />

          {/* Progress fill — driven by var(--p) */}
          <Box
            sx={{
              position: "absolute",
              left: `${TRACK_PADDING_X}px`,
              top: "50%",
              height: 4,
              width: `calc((100% - ${TRACK_PADDING_X * 2}px) * var(--p, 0))`,
              backgroundColor: "var(--accent)",
              borderRadius: 999,
              transform: "translateY(-50%)",
              transition: isPlaying ? "none" : "width 100ms linear",
              willChange: "width",
            }}
          />

          {/* Playhead — driven by var(--p) */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: `calc(${TRACK_PADDING_X}px + (100% - ${TRACK_PADDING_X * 2}px) * var(--p, 0))`,
              transform: "translate(-50%, -50%)",
              width: 14,
              height: 14,
              borderRadius: "50%",
              backgroundColor: "var(--accent)",
              boxShadow: "0 0 0 3px rgba(255,255,255,0.85), 0 4px 12px rgba(0,0,0,0.35)",
              opacity: hasEvents ? 1 : 0,
              transition: isPlaying ? "none" : "left 100ms linear",
              willChange: "left",
              pointerEvents: "none",
              zIndex: 5,
            }}
          />

          {/* Dots — rendered ONCE; CSS-only active state via data-active.
              Connector length scales with the available vertical space so
              taller tracks spread dots farther from the rail. */}
          {(() => {
            // Vertical gap from the rail centerline to each dot edge.
            const dotGap = 18;
            return items.list.map((it, i) => {
            const c = getColor ? getColor(it) : accent;
            const side = i % 2 === 0 ? "top" : "bottom";
            const dot = (
              <Box
                ref={(node) => {
                  if (node) dotsRef.current.set(it.id, node);
                  else dotsRef.current.delete(it.id);
                }}
                data-active="false"
                onClick={() => {
                  stop();
                  seek(it._p);
                }}
                sx={{
                  position: "absolute",
                  top: side === "top"
                    ? `calc(50% - ${dotGap}px - ${DOT_SIZE}px)`
                    : `calc(50% + ${dotGap}px)`,
                  left: `calc(${TRACK_PADDING_X}px + (100% - ${TRACK_PADDING_X * 2}px) * ${it._p})`,
                  transform: "translateX(-50%)",
                  width: DOT_SIZE,
                  height: DOT_SIZE,
                  borderRadius: "50%",
                  backgroundColor: c,
                  border: "2px solid transparent",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                  cursor: "pointer",
                  transition: "transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease",
                  // Hair-thin connector to the rail. Stops at the rail edge
                  // (not the centerline) so it never crosses the progress bar.
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: "50%",
                    width: "1px",
                    height: `${dotGap - 4}px`,
                    backgroundColor: c,
                    opacity: 0.35,
                    transform: "translateX(-50%)",
                    pointerEvents: "none",
                    ...(side === "top"
                      ? { top: "100%" }
                      : { bottom: "100%" }),
                  },
                  "&[data-active='true']": {
                    transform: "translateX(-50%) scale(1.35)",
                    borderColor: "#fff",
                    boxShadow: `0 0 0 3px ${c}, 0 4px 12px rgba(0,0,0,0.35)`,
                    zIndex: 4,
                  },
                  "&:hover": { transform: "translateX(-50%) scale(1.25)" },
                }}
              />
            );

            return renderTooltip ? (
              <Tooltip arrow key={it.id} title={renderTooltip(it)}>
                {dot}
              </Tooltip>
            ) : (
              <Box key={it.id}>{dot}</Box>
            );
            });
          })()}
        </Box>
      </Box>

      {/* ── Endpoints (just min/max timestamps under the track) ───────── */}
      {showScrubber && hasEvents && (
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5, px: `${TRACK_PADDING_X}px` }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
            {formatTs(items.minTs)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
            {formatTs(items.maxTs)}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

// ── Helpers ──────────────────────────────────────────────────────────────────
function formatTs(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export default TimelinePlayer;
